import type { FC, PropsWithChildren } from 'react';
import type { RootState } from '@/store/store';
//
import { useLayoutEffect, useState } from 'react';
import { useAppDispatch } from '@/store/hooks';
import { useSelector } from 'react-redux';
import { me } from '@/store/auth/authSlice';
import { Spinner } from 'flowbite-react';
import { getClients } from '@/store/organization/clientSlice';
import { getProjects } from '@/store/organization/projectSlice';
import { getKeywordGroups } from '@/store/organization/keywordGroupSlice';
import { getCategoryGroups } from '@/store/organization/categoryGroupSlice';
import { getBrandGroups } from '@/store/organization/brandGroupSlice';
import { getOrganizations } from '@/store/organization/organizationSlice';
import { StatusEnum } from '@/store/common-types';

const LoaderLayout: FC<{ isLoadingAuth: boolean }> = ({ isLoadingAuth }) => {
  const { status } = useSelector((state: RootState) => state.auth);

  const { status: clientStatus } = useSelector(
    (state: RootState) => state.clients
  );
  const { status: projectStatus } = useSelector(
    (state: RootState) => state.projects
  );
  const { status: keywordGroupsStatus } = useSelector(
    (state: RootState) => state.keywordGroups
  );
  const { status: categoryGroupsStatus } = useSelector(
    (state: RootState) => state.categoryGroups
  );
  const { status: brandGroupsStatus } = useSelector(
    (state: RootState) => state.brandGroups
  );

  const { status: organizationStatus } = useSelector(
    (state: RootState) => state.organization
  );

  const isLoading =
    !isLoadingAuth &&
    (clientStatus === StatusEnum.loading ||
      keywordGroupsStatus === StatusEnum.loading ||
      categoryGroupsStatus === StatusEnum.loading ||
      brandGroupsStatus === StatusEnum.loading ||
      projectStatus === StatusEnum.loading ||
      organizationStatus === StatusEnum.loading ||
      status === StatusEnum.loading);
  if (isLoading)
    return (
      <div className="absolute z-10 flex h-screen w-screen items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  return null;
};

const AuthProvider = ({ children }: PropsWithChildren) => {
  const dispatch = useAppDispatch();

  const { meStatus, isAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );

  const [isLoadingAuth, setIsLoadingAuth] = useState(true);

  const selectedOrganization = useSelector(
    (state: RootState) => state.organization.selectedOrganization
  );

  useLayoutEffect(() => {
    if (meStatus === StatusEnum.failed) return setIsLoadingAuth(false);

    if (meStatus === StatusEnum.loading) setIsLoadingAuth(true);

    if (!isAuthenticated && meStatus === StatusEnum.idle) dispatch(me());

    if (isAuthenticated && !selectedOrganization?.slug) {
      dispatch(getOrganizations());
    }

    if (isAuthenticated && selectedOrganization?.slug) {
      const { slug } = selectedOrganization;
      Promise.all([
        dispatch(getClients({ slug })),
        dispatch(getProjects({ slug })),
        dispatch(getKeywordGroups({ slug })),
        dispatch(
          getCategoryGroups({
            slug,
          })
        ),
        dispatch(getBrandGroups({ slug })),
      ]).finally(() => setIsLoadingAuth(false));
    }
  }, [dispatch, isAuthenticated, selectedOrganization, meStatus]);

  if (isLoadingAuth) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  return (
    <>
      <LoaderLayout isLoadingAuth={isLoadingAuth} />
      {children}
    </>
  );
};

export default AuthProvider;
